import type { ComponentPropsWithRef, MouseEvent } from "react";
import { forwardRef, useCallback } from "react";
import { Column, Link, Row, Section } from "@react-email/components";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import { useParseStyles } from "../../../Design/useParseStyles";
import { defaultStyles } from "./defaultStyles";

interface ButtonStylesSet {
  default: Record<string, StyleValue>;
  hover: Record<string, StyleValue>;
  margin: StyleValue;
  padding: StyleValue;
  sizing: Record<string, StyleValue>;
}

interface ButtonProps extends ComponentPropsWithRef<"a"> {
  "data-preview-mode": boolean;
  href: string;
  styles: ButtonStylesSet;
  text: string;
}

export const SecondaryButton = forwardRef<HTMLAnchorElement, ButtonProps>(
  (
    {
      text,
      href,
      styles,
      onClick,
      "data-preview-mode": isPreviewMode,
      ...rest
    },
    ref,
  ) => {
    const inlineStyles = useParseStyles({
      baseStyles: {
        display: "inline-block",
        borderStyle: "solid",
        textDecoration: "none",
        textAlign: "center",
      },
      styles: {
        ...styles.default,
        ...styles.sizing,
        margin: styles.margin,
        padding: styles.padding,
      },
      defaultStyles: {
        ...defaultStyles.default,
        ...defaultStyles.sizing,
        margin: defaultStyles.margin,
        padding: defaultStyles.padding,
      },
    });

    const handleClick = useCallback(
      (event: MouseEvent<HTMLAnchorElement>) => {
        if (isPreviewMode) event.preventDefault();
        onClick?.(event);
      },
      [isPreviewMode, onClick],
    );

    const defaultSectionProps = {
      align: "auto",
      width: "auto",
    };

    return (
      <Link ref={ref} href={href} onClick={handleClick} {...rest}>
        <Section {...defaultSectionProps}>
          <Row>
            <Column style={inlineStyles}>{text}</Column>
          </Row>
        </Section>
      </Link>
    );
  },
);

SecondaryButton.displayName = "SecondaryButton";
